import { useState, useEffect, useRef } from 'react';
import Modal from '../shared/Modal';
import { upsertAssignment, checkConflict, sendAssignmentEmail } from '../../utils/api';
import { useAuth } from '../../hooks/useAuth';
import { canUserSend } from '../../config';

// Bucket a block start time into a serving slot — must match the backend
// (_slotForTime in Code.gs): AM < 12:00, PM 12:00–16:59, Eve ≥ 17:00.
function slotForTime(t) {
  if (!t) return '';
  const [h, m] = String(t).split(':').map(Number);
  if (isNaN(h)) return '';
  const mins = h * 60 + (m || 0);
  if (mins < 12 * 60) return 'AM';
  if (mins < 17 * 60) return 'PM';
  return 'Eve';
}

export default function AssignPersonModal({
  open, onClose, showToast,
  assignmentId, positionLabel, blockId, blockTitle, teamId, teamName,
  teamPeople, blockAssignments,
  activeSubRole,
  availabilityMap, blockDay, blockStartTime,
  kidsAvail, kidsAssignedCounts,
  onAssign,
  currentPersonName,
  onRemove,
}) {
  const { user } = useAuth();

  // Which day/slot this block falls in, and a helper to test a person's pick.
  const availKey = blockDay && blockStartTime ? `${blockDay}|${slotForTime(blockStartTime)}` : null;
  const isAvail = (personId) =>
    !!(availKey && availabilityMap && availabilityMap[personId] &&
       Array.isArray(availabilityMap[personId].selected) &&
       availabilityMap[personId].selected.includes(availKey));

  // Kids "most helpful 3": did this SL check THIS session as free, and have
  // they already hit the number of sessions they said they could serve?
  // Hitting the max only grays them out — a verbal "sure, I'll do more" is an
  // override, so the row stays clickable.
  // Availability selections are FIXED session slots ("Friday|Morning"), so a
  // block maps to its slot by day + start time (noon cutoff, kids rule).
  const isKids = !!kidsAvail;
  const kidsSlotKey = (blockDay && blockStartTime)
    ? `${blockDay}|${(parseInt(blockStartTime, 10) || 0) < 12 ? 'Morning' : 'Evening'}`
    : null;
  const kidsRec = (personId) => (kidsAvail && kidsAvail[personId]) || null;
  const kidsFree = (personId) => {
    const r = kidsRec(personId);
    if (!r || !Array.isArray(r.selected)) return false;
    return r.selected.includes(blockId) || (!!kidsSlotKey && r.selected.includes(kidsSlotKey));
  };
  const kidsMaxInfo = (personId) => {
    const r = kidsRec(personId);
    const max = r ? parseInt(r.maxSessions, 10) || 0 : 0;
    const assigned = (kidsAssignedCounts && kidsAssignedCounts[personId]) || 0;
    return { max, assigned, maxed: max > 0 && assigned >= max };
  };
  const [selected, setSelected] = useState(null);
  const [query, setQuery] = useState('');
  const [conflict, setConflict] = useState(null);
  const [checkingConflict, setCheckingConflict] = useState(false);
  const [overrideConflict, setOverrideConflict] = useState(false);
  // Guards conflict-check races: clicking A then B fast, or closing the modal
  // mid-check, must not let the older response paint B (or the next open) with
  // A's conflict.
  const conflictSeqRef = useRef(0);

  const canSend = canUserSend(user?.email, user?.role);

  // People already placed in THIS block (for display)
  const placedPersonIds = new Set(
    (blockAssignments || [])
      .filter(a => a.personId && a.id !== assignmentId)
      .map(a => a.personId)
  );

  // When in a role roster, only show people assigned that role
  const rosterPool = activeSubRole
    ? (teamPeople || []).filter(p => (p.roles || []).includes(activeSubRole))
    : (teamPeople || []);

  // People available this block surface first when availability data exists.
  // Kids ordering: free-for-this-session first, then not-yet-maxed, then maxed.
  const available = rosterPool
    .filter(p => !placedPersonIds.has(p.id))
    .sort((a, b) => {
      if (isKids) {
        const score = (p) => (kidsFree(p.id) ? 2 : 0) + (kidsMaxInfo(p.id).maxed ? -3 : 0);
        return score(b) - score(a);
      }
      return (isAvail(b.id) ? 1 : 0) - (isAvail(a.id) ? 1 : 0);
    });
  const placed = rosterPool.filter(p => placedPersonIds.has(p.id));

  // Search across the whole pool — someone already placed in this block can
  // still be given a second position (e.g. FOH Audio + Audio Lead).
  const searchFiltered = query.length > 1
    ? [...available, ...placed].filter(p => p.name.toLowerCase().includes(query.toLowerCase()))
    : [];

  useEffect(() => {
    if (!open) {
      conflictSeqRef.current++; // orphan any in-flight conflict check
      setSelected(null);
      setQuery('');
      setConflict(null);
      setCheckingConflict(false);
      setOverrideConflict(false);
    }
  }, [open]);

  function handleSelect(person) {
    const seq = ++conflictSeqRef.current;
    setSelected(person);
    setQuery('');
    setConflict(null);
    setOverrideConflict(false);

    if (blockId && person.id) {
      setCheckingConflict(true);
      checkConflict(person.id, blockId)
        .then(result => {
          if (seq !== conflictSeqRef.current) return; // stale — a newer selection happened
          if (result && result.conflict) setConflict(result.conflicts);
        })
        .catch(err => console.error('Conflict check failed:', err))
        .finally(() => { if (seq === conflictSeqRef.current) setCheckingConflict(false); });
    }
  }

  async function handleAssign(sendRequest) {
    if (!selected) return;

    const assignment = {
      id: assignmentId || crypto.randomUUID(),
      blockId,
      teamId,
      personId: selected.id,
      positionLabel: positionLabel || '',
      status: sendRequest ? 'pending' : 'unsent',
      sentAt: sendRequest ? new Date().toISOString() : '',
      respondedAt: '',
      notes: '',
    };

    // Update UI and close immediately
    showToast(sendRequest
      ? `${selected.name} assigned — schedule request sent.`
      : `${selected.name} assigned.`
    );
    onClose();
    if (onAssign) onAssign(assignment);

    // Persist in background
    try {
      await upsertAssignment(assignment);
      if (sendRequest) {
        try {
          await sendAssignmentEmail(
            selected.email,
            selected.name,
            selected.id,
            blockTitle || positionLabel || 'a block',
            teamName || '',
            user?.email,
            isKids ? 'kids-schedule-assignment' : undefined,
          );
        } catch (emailErr) {
          console.error('Email failed:', emailErr);
          showToast(`Email failed for ${selected.name} — check Apps Script logs.`);
        }
      }
    } catch (err) {
      showToast('Error saving assignment — please refresh.');
      console.error(err);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Fill Position"
      footer={
        <>
          <button className="btn" onClick={onClose}>Cancel</button>
          {selected && (() => {
            // Block assigning until the conflict check answers — clicking fast
            // must never beat the warning popup.
            const blocked = checkingConflict || (!!(conflict?.length) && !overrideConflict);
            return (
              <>
                <button className="btn" onClick={() => handleAssign(false)} disabled={blocked}>
                  {checkingConflict ? 'Checking…' : 'Assign Only'}
                </button>
                {canSend && (
                  <button className="btn btn-dark" onClick={() => handleAssign(true)} disabled={blocked}>
                    {checkingConflict ? 'Checking…' : 'Assign & Send Request'}
                  </button>
                )}
                {!canSend && (
                  <button className="btn btn-dark" onClick={() => handleAssign(false)} disabled={blocked}>
                    {checkingConflict ? 'Checking…' : 'Assign'}
                  </button>
                )}
              </>
            );
          })()}
        </>
      }
    >
      {positionLabel && (
        <div style={{ fontFamily: 'var(--font-b)', fontSize: 13, fontWeight: 300, color: 'var(--muted)', marginBottom: 16 }}>
          {positionLabel}
        </div>
      )}

      {/* Currently assigned person — shown when opening via "Change" */}
      {currentPersonName && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '10px 14px', marginBottom: 16,
          background: 'var(--off)', border: '1px solid var(--border)', borderRadius: 'var(--radius)',
        }}>
          <div>
            <div style={{ fontFamily: 'var(--font-h)', fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 3 }}>
              Currently Assigned
            </div>
            <div style={{ fontFamily: 'var(--font-h)', fontSize: 14, fontWeight: 800, color: 'var(--black)' }}>
              {currentPersonName}
            </div>
          </div>
          <button
            title="Remove from position"
            onClick={() => { if (onRemove) onRemove(); onClose(); }}
            style={{
              background: 'none', border: '1px solid var(--border)', borderRadius: 'var(--radius)',
              padding: '5px 10px', cursor: 'pointer', color: 'var(--muted)',
              fontFamily: 'var(--font-h)', fontSize: 12, fontWeight: 700,
              display: 'flex', alignItems: 'center', gap: 5,
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = '#c00'; e.currentTarget.style.color = '#c00'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--muted)'; }}
          >
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
              <path d="M1.5 1.5l7 7M8.5 1.5l-7 7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
            </svg>
            Remove
          </button>
        </div>
      )}

      {activeSubRole && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, padding: '7px 12px', background: 'var(--off)', border: '1px solid var(--border)', borderRadius: 'var(--radius)' }}>
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><circle cx="6" cy="6" r="5" stroke="currentColor" strokeWidth="1.4"/><path d="M6 5v4M6 3.5v.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg>
          <span style={{ fontFamily: 'var(--font-b)', fontSize: 12, fontWeight: 300, color: 'var(--muted)' }}>
            Showing <strong style={{ fontFamily: 'var(--font-h)', color: 'var(--black)' }}>{activeSubRole}</strong> people only
          </span>
        </div>
      )}

      <div className="form-group" style={{ marginBottom: 12 }}>
        <label className="form-label">Search Your Roster</label>
        <div className="search-dropdown">
          <input
            className="form-input"
            placeholder="Type a name to search…"
            value={query}
            onChange={e => setQuery(e.target.value)}
            autoComplete="off"
          />
          {searchFiltered.length > 0 && (
            <div className="search-results">
              {searchFiltered.map(p => (
                <div key={p.id} className="search-result-item" onClick={() => handleSelect(p)}>
                  <div className="search-result-name">{p.name}</div>
                  <div className="search-result-meta">{p.email}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {conflict && conflict.length > 0 && (
        <div style={{ background: overrideConflict ? '#fffbf0' : '#fff0f0', border: `1px solid ${overrideConflict ? '#d4920a' : '#900'}`, borderRadius: 'var(--radius)', padding: '12px 16px', marginBottom: 12 }}>
          <div style={{ fontFamily: 'var(--font-h)', fontSize: 12, fontWeight: 700, color: overrideConflict ? '#8a6000' : '#900', marginBottom: 6 }}>
            {overrideConflict ? '⚠ Conflict overridden' : '⚠ Scheduling Conflict'}
          </div>
          {conflict.map((c, i) => (
            <div key={i} style={{ fontFamily: 'var(--font-b)', fontSize: 13, fontWeight: 300, color: overrideConflict ? '#7a4f0a' : '#700', marginBottom: 4 }}>
              {selected?.name} is already scheduled on <strong>{c.teamName || 'another team'}</strong> — <strong>{c.blockTitle}</strong> on {c.date}
              {c.startTime ? ` (${c.startTime}–${c.endTime || 'end'})` : ''}.
            </div>
          ))}
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={overrideConflict}
              onChange={e => setOverrideConflict(e.target.checked)}
              style={{ accentColor: 'var(--black)', flexShrink: 0 }}
            />
            <span style={{ fontFamily: 'var(--font-h)', fontSize: 12, fontWeight: 700, color: 'var(--black)' }}>
              Assign anyway — I've confirmed this is okay
            </span>
          </label>
        </div>
      )}

      {checkingConflict && (
        <div style={{ fontFamily: 'var(--font-b)', fontSize: 13, color: 'var(--muted)', marginBottom: 12 }}>
          Checking for conflicts…
        </div>
      )}

      <div style={{ fontFamily: 'var(--font-h)', fontSize: 11, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 8 }}>
        {activeSubRole ? `${activeSubRole} — Available This Block` : 'Your Roster — Available This Block'}
      </div>
      <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden', maxHeight: 280, overflowY: 'auto' }}>
        {available.length === 0 && (
          <div style={{ padding: '16px', fontFamily: 'var(--font-b)', fontSize: 13, fontWeight: 300, color: 'var(--muted)', textAlign: 'center' }}>
            {activeSubRole
              ? `No ${activeSubRole} people available — assign roles in the People tab first.`
              : 'No available roster members — add people to your roster first.'}
          </div>
        )}
        {available.map(p => {
          const mi = isKids ? kidsMaxInfo(p.id) : null;
          return (
            <div
              key={p.id}
              className={`assign-roster-row${selected?.id === p.id ? ' selected-row' : ''}${mi?.maxed ? ' unavailable' : ''}`}
              onClick={() => handleSelect(p)}
            >
              <div style={{ flex: 1 }}>
                <div className="assign-roster-name">{p.name}</div>
                <div className="assign-roster-meta">{p.email}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                {isKids && kidsFree(p.id) && !mi?.maxed && (
                  <span
                    title="Checked this session on their availability form"
                    style={{ fontFamily: 'var(--font-h)', fontSize: 9, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', padding: '3px 8px', borderRadius: 100, background: '#D8F0EC', color: '#1A5048' }}
                  >
                    ✓ Free this session
                  </span>
                )}
                {mi?.maxed && (
                  <span
                    title={`Said they could serve ${mi.max} — already assigned to ${mi.assigned}. You can still assign (override).`}
                    style={{ fontFamily: 'var(--font-h)', fontSize: 9, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', padding: '3px 8px', borderRadius: 100, background: 'var(--off)', color: 'var(--muted)', border: '1px solid var(--border)' }}
                  >
                    Max hit ({mi.assigned}/{mi.max})
                  </span>
                )}
                {!isKids && isAvail(p.id) && (
                  <span
                    title={availabilityMap?.[p.id]?.note || 'Marked available for this day & time'}
                    style={{ fontFamily: 'var(--font-h)', fontSize: 9, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', padding: '3px 8px', borderRadius: 100, background: '#D4E8DF', color: '#2D6B55' }}
                  >
                    ✓ Available
                  </span>
                )}
                {(p.registered ?? /^\d+$/.test(String(p.brushfireId || '').trim())) && !/clear/i.test(p.bgCheck || '') && (
                  <span
                    title="Registered but no cleared background check on file"
                    className="pill"
                    style={{ fontSize: 9, padding: '3px 8px', background: '#fee', color: '#b00000', border: '1px solid #f5c2c2' }}
                  >
                    No BG Check
                  </span>
                )}
                <span className={`pill ${(p.registered ?? /^\d+$/.test(String(p.brushfireId || '').trim())) ? 'pill-green' : 'pill-amber'}`} style={{ fontSize: 9, padding: '3px 8px' }}>
                  {(p.registered ?? /^\d+$/.test(String(p.brushfireId || '').trim())) ? 'Registered' : 'Not Registered'}
                </span>
              </div>
            </div>
          );
        })}
        {placed.length > 0 && (
          <>
            <div style={{ padding: '8px 16px', fontFamily: 'var(--font-h)', fontSize: 10, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--subtle)', background: 'var(--off)', borderTop: '1px solid var(--border)' }}>
              Already Placed This Block — tap to add a second position
            </div>
            {placed.map(p => (
              <div
                key={p.id}
                className={`assign-roster-row${selected?.id === p.id ? ' selected-row' : ''}`}
                style={{ opacity: selected?.id === p.id ? 1 : 0.6 }}
                onClick={() => handleSelect(p)}
              >
                <div>
                  <div className="assign-roster-name">{p.name}</div>
                  <div className="assign-roster-meta">{p.email}</div>
                </div>
                <span className="pill pill-gray" style={{ fontSize: 9, padding: '3px 8px' }}>Placed</span>
              </div>
            ))}
          </>
        )}
      </div>

      {selected && (
        <div style={{ marginTop: 12, padding: '12px 16px', background: 'var(--off)', border: '1px solid var(--border)', borderRadius: 'var(--radius)' }}>
          <div style={{ fontFamily: 'var(--font-h)', fontSize: 11, fontWeight: 700, letterSpacing: '0.15em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 4 }}>Selected</div>
          <div style={{ fontFamily: 'var(--font-h)', fontSize: 14, fontWeight: 800, color: (conflict?.length && !overrideConflict) ? '#900' : 'var(--black)' }}>{selected.name}</div>
          {placedPersonIds.has(selected.id) && (
            <div style={{ fontFamily: 'var(--font-b)', fontSize: 12, color: 'var(--mid)', marginTop: 2 }}>
              Already has a position in this block — this adds a second one.
            </div>
          )}
          {selected.brushfireId && !/clear/i.test(selected.bgCheck || '') && (
            <div style={{ fontFamily: 'var(--font-b)', fontSize: 12, color: '#b00000', marginTop: 2 }}>
              ⚠ No cleared background check on file for {selected.name}.
            </div>
          )}
          {isKids && (
            <div style={{ fontFamily: 'var(--font-b)', fontSize: 12, color: kidsFree(selected.id) ? '#1A5048' : 'var(--muted)', marginTop: 2 }}>
              {kidsFree(selected.id) ? '✓ Checked this session on their availability form' : 'Did not check this session on their availability form'}
              {(() => { const mi = kidsMaxInfo(selected.id); return mi.max > 0 ? ` · assigned to ${mi.assigned} of ${mi.max} sessions${mi.maxed ? ' — assigning is an override' : ''}` : ''; })()}
            </div>
          )}
          {!isKids && availKey && (
            <div style={{ fontFamily: 'var(--font-b)', fontSize: 12, color: isAvail(selected.id) ? '#2D6B55' : 'var(--muted)', marginTop: 2 }}>
              {isAvail(selected.id) ? '✓ Marked available for this day & time' : 'Has not marked this day & time as available'}
            </div>
          )}
          {availabilityMap?.[selected.id]?.note && (
            <div style={{ fontFamily: 'var(--font-b)', fontSize: 12, fontWeight: 300, color: 'var(--mid)', marginTop: 4, fontStyle: 'italic' }}>
              “{availabilityMap[selected.id].note}”
            </div>
          )}
          {conflict?.length > 0 && !overrideConflict && (
            <div style={{ fontFamily: 'var(--font-b)', fontSize: 12, color: '#900', marginTop: 2 }}>Check the override box above to assign despite conflict.</div>
          )}
        </div>
      )}
    </Modal>
  );
}
